// The in-match HUD sound toggle (client-audio spec R9 / design DD6/DD7/DD8; tasks.md 6.2).
// Promoted from `prototype/sketches/client-audio/*` into the shared kit
// (`prototype/components/hud/SoundControl.{jsx,d.ts,prompt.md}`, task 6.3) and ported 1:1 here,
// styled only from token CSS vars (mirroring `SeasonClock`/`ResourceCounter`).
//
// It renders the two-axis autoplay lifecycle's FOUR render states (enabled-but-locked /
// playing / muted / suspended-as-locked, DD5) with an inline pixel-snapped SVG speaker glyph
// (DD7 — extending the UI.md §2 `✕ ⌂ +` glyph family with no icon font / no binary asset) and
// the inline coarse notch master-volume level (DD8 — taps cycle 0/low/mid/full in place).
//
// Injectable seam (DD6, mirroring `INERT_CONTROLLER`/`INERT_LOD`): `lifecycle`/`prefs` are
// optional with an INERT default, so a bare mount renders the boot-locked affordance without a
// live engine/session and never crashes (the "renders in the HUD without a session" scenario).
// A UI-interaction blip is fired via the injected engine at the tap callback — NOT off a store
// edge (spec R9) — so it plays as immediate feedback for the gesture. Because jsdom produces no
// assertable waveform, tests prove the wiring with a recording fake engine; the audible surface
// is the EXCUSED manual browser gate (task 8.4).
//
// Reactivity: the component subscribes to the lifecycle + prefs subscribables and caches each
// snapshot in local state (NOT `useSyncExternalStore` — `getState`/`get` return a fresh object
// each call, so a snapshot-identity check would loop; caching sidesteps that, review-2 g4 note).
// Re-renders are bounded by lifecycle dispatches (user gestures + browser `statechange`) and
// prefs writes, never per-frame.

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { NullAudioEngine } from '../../../audio/engine';
import type { AudioEngine } from '../../../audio/engine';
import type { AudioLifecycle, LifecycleSnapshot } from '../../../audio/lifecycle';
import type { AudioPrefsStore } from '../../../audio/audio-prefs-store';
import { DEFAULT_MASTER_VOLUME } from '../../../audio/audio-prefs-store';

/** The live seam bundle a screen threads into its `SoundControl` mount (Colony/Match, task
 * 6.5): the shared two-axis lifecycle and the durable preference adapter. The composition-root
 * `initAudio` (task group 7) constructs and injects it; a bare screen leaves it undefined and
 * renders no control (the seam-absent, pre-wiring reality — matching `INERT_CONTROLLER`). */
export interface SoundControlSeams {
  lifecycle: AudioLifecycle;
  prefs: AudioPrefsStore;
}

export interface SoundControlProps {
  /** The two-axis autoplay lifecycle (design DD5). Optional with an inert default so a bare
   * mount renders the boot-locked affordance without a live engine/session. */
  lifecycle?: AudioLifecycle;
  /** The durable preference adapter (spec R7) — the master-volume source of truth for the DD8
   * notch. Optional; without it the notch renders the default level and is display-only. */
  prefs?: AudioPrefsStore;
  style?: CSSProperties;
}

/** The coarse master-volume steps the DD8 notch cycles through (0/low/mid/full; the 0 step is a
 * volume mute, distinct from the lifecycle's enable/mute toggle). A presentation constant. */
const VOLUME_STEPS = [0, 0.34, 0.67, 1] as const;

/** The inert-default lifecycle (mirrors `INERT_CONTROLLER`/`INERT_LOD`): a boot-locked snapshot,
 * a no-op tap/subscribe, and a no-op engine, so a `SoundControl` with no `lifecycle` renders the
 * boot-locked affordance and swallows a tap without a live `AudioContext`. */
const INERT_ENGINE: AudioEngine = new NullAudioEngine();
const INERT_LIFECYCLE: AudioLifecycle = {
  getState: () => ({ enabled: false, contextState: 'absent', arming: false, render: 'locked' }),
  tap: () => {},
  subscribe: () => () => {},
  getEngine: () => INERT_ENGINE,
  dispose: () => {},
};

type SpeakerVariant = 'locked' | 'playing' | 'muted';

interface RenderView {
  variant: SpeakerVariant;
  /** Accessible label + visible copy for the toggle in this state (validated in the sketch). */
  label: string;
  /** Whether the coarse master-volume notch shows (only while armed + Running, DD8). */
  showNotch: boolean;
}

/** Map a lifecycle snapshot to the toggle's rendered affordance (DD5 four states). Locked splits
 * into three copies by the runtime axis: browser-suspended → "tap to resume", persisted-on →
 * "tap to turn on", pref-off boot → "turn on sound"; a transient arming shows "starting". */
function describeState(s: LifecycleSnapshot): RenderView {
  if (s.render === 'playing') {
    return { variant: 'playing', label: 'Mute sound', showNotch: true };
  }
  if (s.render === 'muted') {
    return { variant: 'muted', label: 'Unmute sound', showNotch: false };
  }
  // locked (absent / suspended / closed — no usable Running context)
  if (s.arming) {
    return { variant: 'locked', label: 'Starting sound', showNotch: false };
  }
  if (s.contextState === 'suspended') {
    return { variant: 'locked', label: 'Tap to resume sound', showNotch: false };
  }
  if (s.enabled) {
    return { variant: 'locked', label: 'Tap to turn on sound', showNotch: false };
  }
  return { variant: 'locked', label: 'Turn on sound', showNotch: false };
}

/** The nearest coarse step to a stored master volume (so a default like 0.8 snaps into the
 * cycle), then the NEXT step wrapping 0→low→mid→full→0. */
function nextVolumeStep(volume: number): number {
  let nearest = 0;
  for (let i = 1; i < VOLUME_STEPS.length; i += 1) {
    if (Math.abs(VOLUME_STEPS[i] - volume) < Math.abs(VOLUME_STEPS[nearest] - volume)) {
      nearest = i;
    }
  }
  return VOLUME_STEPS[(nearest + 1) % VOLUME_STEPS.length];
}

/** The inline pixel-snapped speaker glyph (DD7). Waves vary by variant: playing = two arcs,
 * locked = one small arc ("quiet"), muted = a slash. No icon font, no binary asset (D2). */
function SpeakerGlyph({ variant }: { variant: SpeakerVariant }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      style={{ imageRendering: 'pixelated', flex: 'none' }}
    >
      <path d="M2 6h2l3-2v8L4 10H2z" fill="currentColor" stroke="none" />
      {variant === 'playing' && (
        <>
          <path d="M10 5.5a3 3 0 0 1 0 5" />
          <path d="M12.5 4a5.5 5.5 0 0 1 0 8" />
        </>
      )}
      {variant === 'locked' && <path d="M10 6.5a2 2 0 0 1 0 3" />}
      {variant === 'muted' && <path d="M10 6l4 4M14 6l-4 4" />}
    </svg>
  );
}

/** The inline coarse master-volume notch (DD8): three bars filled to the current level. Taps
 * cycle the level in place — no popover, no drag.
 *
 * **The staircase is centred in the button, not aligned to the button's own floor.** The button is
 * a 44px hit target inside a `--r-pill` container, so `align-items: flex-end` on the button itself
 * put the three bars on the pill's bottom edge — precisely where a pill radius curves away from
 * them, which is what read as the bars overflowing the control. The bars keep their bottom-aligned
 * staircase; they are just bottom-aligned inside a 14px box that is centred, so the hit target is
 * untouched and the glyph and the notch now sit on one optical line. */
function VolumeNotch({ volume, onCycle }: { volume: number; onCycle: () => void }) {
  const filled = Math.round(volume * 3);
  return (
    <button
      type="button"
      data-testid="sound-control-volume"
      aria-label="Adjust volume"
      onClick={onCycle}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 44,
        minWidth: 44,
        padding: '0 8px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 14 }}>
        {[5, 9, 14].map((h, i) => (
          <span
            key={h}
            style={{
              width: 3,
              height: h,
              borderRadius: 1,
              background: i < filled ? 'var(--accent)' : 'var(--hud-track)',
            }}
          />
        ))}
      </span>
    </button>
  );
}

export function SoundControl({ lifecycle: injected, prefs, style }: SoundControlProps) {
  const lifecycle = injected ?? INERT_LIFECYCLE;

  const [snapshot, setSnapshot] = useState<LifecycleSnapshot>(() => lifecycle.getState());
  useEffect(() => {
    // Re-sync on (re)subscribe in case the state moved between the initial read and the
    // subscription, then follow every dispatch (bounded by gestures + statechange).
    setSnapshot(lifecycle.getState());
    return lifecycle.subscribe(setSnapshot);
  }, [lifecycle]);

  const [volume, setVolume] = useState<number>(() => prefs?.get().masterVolume ?? DEFAULT_MASTER_VOLUME);
  useEffect(() => {
    if (prefs === undefined) return;
    setVolume(prefs.get().masterVolume);
    return prefs.subscribe((p) => setVolume(p.masterVolume));
  }, [prefs]);

  const view = describeState(snapshot);

  const onToggle = (): void => {
    // The gesture drives the lifecycle (enable / unlock / mute is its job); the engine swap
    // happens synchronously inside `tap()` when this is the first unlock, so reading the engine
    // AFTER the tap lands the UI blip on the just-armed engine (spec R9 — a component-tap blip,
    // not a store-edge cue). Before arming the engine is the no-op `NullAudioEngine`, so a
    // pre-unlock blip is correctly silent.
    lifecycle.tap();
    lifecycle.getEngine().sfx('ui-blip');
  };

  const onCycleVolume = (): void => {
    const next = nextVolumeStep(volume);
    setVolume(next);
    prefs?.set({ masterVolume: next });
    const engine = lifecycle.getEngine();
    engine.setMasterVolume(next);
    engine.sfx('ui-blip'); // the notch is a wired UI affordance too (spec R9)
  };

  return (
    <div
      data-testid="sound-control"
      role="group"
      aria-label="Sound"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 44,
        padding: '0 8px 0 12px',
        borderRadius: 'var(--r-pill)',
        background: 'var(--hud-bg)',
        border: `1.5px ${view.variant === 'locked' ? 'dashed' : 'solid'} var(--hud-line)`,
        boxShadow: 'var(--shadow-hud)',
        ...style,
      }}
    >
      <button
        type="button"
        data-testid="sound-control-toggle"
        aria-label={view.label}
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 44,
          // A hit target is an *area*, and in the playing state this button loses its text label to
          // the notch — leaving an 18px glyph and `padding: 0`, i.e. an 18 × 44 tap target for mute
          // on a phone. `--hit-target` on both axes matches the notch beside it (already 44 wide)
          // and the kit's own icon button, which is explicitly square (UI.md §2, `Button` size
          // `icon`). Costs ~26px of pill width in that one state; a mute control that is hard to
          // hit is the worse trade.
          minWidth: 44,
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 13,
          color:
            view.variant === 'playing'
              ? 'var(--accent)'
              : view.variant === 'muted'
                ? 'var(--hud-text-dim)'
                : 'var(--accent-strong)',
        }}
      >
        <SpeakerGlyph variant={view.variant} />
        {/* Playing shows the notch instead of a text label (the sketch's compact HUD form);
            the other states carry their copy so the affordance reads at a glance. */}
        {!view.showNotch && <span>{view.label}</span>}
      </button>
      {view.showNotch && <VolumeNotch volume={volume} onCycle={onCycleVolume} />}
    </div>
  );
}
